import { pickBy, isEqual, groupBy, mapValues, mapKeys, fromPairs } from "lodash";

export const filterEqual = (v, filterValue) => filterValue === null || filterValue === undefined || v == filterValue;
export const filterDateRange = (v, filterValue) => {
  const time = v && moment.parseZone(v);
  return (
    !filterValue ||
    (true &&
      (!filterValue.gte || (time && time.isSameOrAfter(filterValue.gte, "day"))) &&
      (!filterValue.gt || (time && time.isAfter(filterValue.gt, "day"))) &&
      (!filterValue.lte || (time && time.isSameOrBefore(filterValue.lte, "day"))) &&
      (!filterValue.lt || (time && time.isBefore(filterValue.lt, "day"))) &&
      (!filterValue.eq || (time && time.isSame(filterValue.eq, "day"))) &&
      (!filterValue.not_eq || (time && !time.isSame(filterValue.not_eq, "day"))))
  );
};
export const filterIncludes = (v, filterValue) =>
  !filterValue || filterValue.includes("all") || filterValue.includes(v);
export const filterQuery = (v, filterValue) =>
  !filterValue || (v && v.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1);

export function aggregateBy(allItems, filters, filterFn, filterKey, prop = filterKey) {
  const filteredItems = filterFn(allItems, { ...filters, [filterKey]: null });
  return {
    all: filteredItems.length,
    ...mapValues(groupBy(filteredItems, prop), (items) => items.length),
  };
}

export function aggregateByBooleans(allItems, filters, filterFn, filterKey, props) {
  const filteredItems = filterFn(allItems, { ...filters, [filterKey]: null });
  return {
    all: filteredItems.length,
    ...fromPairs(props.map((prop) => [prop, filteredItems.filter((item) => item[prop]).length])),
  };
}

const normalizeFilterValue = (filterValue, filterType) => {
  switch (filterType) {
    case "String[]":
      return filterValue
        ? Array.isArray(filterValue)
          ? filterValue.length > 0
            ? filterValue
            : null
          : [filterValue]
        : null;
    case "Date":
      return filterValue && Object.values(filterValue).filter((x) => !!x).length > 0 ? filterValue : null;
    case "Boolean":
      return filterValue;
    case "String":
      return filterValue || null;
    case "Number":
      return filterValue !== undefined && filterValue !== null && filterValue !== "" ? +filterValue : null;
    default:
      throw new Error(`Unknown filterType: ${filterType}`);
  }
};

const serializeFilterValue = (filterKey, filterType, filterValue) => {
  switch (filterType) {
    case "String[]":
    case "Boolean":
    case "String":
    case "Number":
      return { [filterKey]: filterValue };
    case "Date":
      return filterValue
        ? mapKeys(
            pickBy(filterValue, (v) => !!v),
            (v, k) => `${filterKey}_${k}`
          )
        : { [filterKey]: filterValue };
    default:
      throw new Error(`Unknown filterType: ${filterType}`);
  }
};

const deserializeFilterValue = (query, filterKey, filterType) => {
  switch (filterType) {
    case "String":
    case "String[]":
      return query[filterKey] !== undefined ? query[filterKey] || null : undefined;
    case "Boolean":
      return query[filterKey] === "true" ? true : query[filterKey] === "false" ? false : undefined;
    case "Number":
      return query[filterKey] !== undefined
        ? query[filterKey] !== null && query[filterKey] !== ""
          ? +query[filterKey]
          : null
        : undefined;
    case "Date":
      const filterValue = pickBy(
        {
          gte: query[`${filterKey}_gte`],
          gt: query[`${filterKey}_gt`],
          lte: query[`${filterKey}_lte`],
          lt: query[`${filterKey}_lt`],
          eq: query[`${filterKey}_eq`],
          not_eq: query[`${filterKey}_not_eq`],
        },
        (v) => !!v
      );
      return query[filterKey] === null ? null : Object.keys(filterValue).length > 0 ? filterValue : undefined;
    default:
      throw new Error(`Unknown filterType: ${filterType}`);
  }
};

const createNormalizeFiltersFn = (filterTypes) => (filters) =>
  mapValues(filterTypes, (filterType, filterKey) => normalizeFilterValue(filters[filterKey], filterType));

const createDeserializeLocationQueryToFiltersFn = (filterTypes) => (query) =>
  pickBy(
    mapValues(filterTypes, (filterType, filterKey) => deserializeFilterValue(query, filterKey, filterType)),
    (filterValue) => filterValue !== undefined
  );

const createSerializeFiltersToLocationQuery = (filterTypes, initialFilters) => (filters) => {
  const filtersToSerialize = isEqual(filters, initialFilters)
    ? {}
    : pickBy(filters, (filterValue, prop) => !isEqual(filterValue, initialFilters[prop]));
  let query = {};
  mapValues(filtersToSerialize, (filterValue, filterKey) => {
    const filterType = filterTypes[filterKey];
    query = { ...query, ...serializeFilterValue(filterKey, filterType, filterValue) };
  });
  return query;
};

export function createFiltersInstance({ SET_FILTERS, initialFilters = {}, filterTypes = {} }) {
  const normalizeFilters = createNormalizeFiltersFn(filterTypes);
  const deserializeLocationQueryToFilters = createDeserializeLocationQueryToFiltersFn(filterTypes);
  const serializeFiltersToLocationQuery = createSerializeFiltersToLocationQuery(filterTypes, initialFilters);

  const initialState = {
    filters: initialFilters,
  };

  const reducersMap = {
    [SET_FILTERS]: (state, { filters }) => ({
      ...state,
      filters: normalizeFilters(filters),
    }),
  };

  const epics = [];

  function setFilters(filters) {
    return { type: SET_FILTERS, filters };
  }

  function setFiltersWithLocationQuery(query) {
    const filters = query ? deserializeLocationQueryToFilters(query) : {};
    console.info({ query, filters });
    return setFilters({ ...initialFilters, ...filters });
  }

  return {
    initialState,
    reducersMap,
    epics,
    actions: {
      setFilters,
      setFiltersWithLocationQuery,
    },
    _serializeFiltersToLocationQuery: serializeFiltersToLocationQuery,
  };
}

export default function createFilteredItemsInstance({
  SET_FILTERS,
  REFILTER,
  stateSelector,
  allItemsStateSelector = (state) => state.allItems.model || [],
  filteredItemsStateKey = "filteredItems",
  initialFilters = {},
  filterTypes = {},
  filterFn = (items, filters) => items,
  initialAggregations = {},
  aggregateFn = (items, filters) => initialAggregations,
}) {
  const normalizeFilters = createNormalizeFiltersFn(filterTypes);
  const deserializeLocationQueryToFilters = createDeserializeLocationQueryToFiltersFn(filterTypes);
  const serializeFiltersToLocationQuery = createSerializeFiltersToLocationQuery(filterTypes, initialFilters);

  const initialState = {
    [filteredItemsStateKey]: [],
    filters: initialFilters,
    aggregations: initialAggregations,
  };

  const reducersMap = {
    [SET_FILTERS]: (state, { filters }) => ({
      ...state,
      filters: normalizeFilters(filters),
    }),

    [REFILTER]: (state) => {
      return {
        ...state,
        [filteredItemsStateKey]: filterFn(allItemsStateSelector(state), state.filters),
        aggregations: aggregateFn(allItemsStateSelector(state), state.filters, filterFn),
      };
    },
  };

  const epics = [];

  function setFilters(filters) {
    return { type: SET_FILTERS, filters };
  }

  function setFiltersWithLocationQuery(query) {
    const filters = query ? deserializeLocationQueryToFilters(query) : {};
    return setFilters({ ...initialFilters, ...filters });
  }

  return {
    initialState,
    reducersMap,
    epics,
    actions: {
      setFilters,
      setFiltersWithLocationQuery,
    },
    _serializeFiltersToLocationQuery: serializeFiltersToLocationQuery,
  };
}



// WEBPACK FOOTER //
// ./src/modules/utils/createFilteredItemsInstance.js